import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate as useNav, useSearchParams } from "react-router-dom";
import {
  Box, Stack, Typography, Button, IconButton, Tooltip,
  Avatar, Chip, Drawer, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, InputAdornment, List, ListItemButton, ListItemText,
  Paper, ClickAwayListener, Popover, Checkbox, FormControlLabel, FormGroup,
  useMediaQuery, useTheme,
} from "@mui/material";
import { IoClose } from "react-icons/io5";
import { DatePicker as MuiDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CheckOutlined } from "@mui/icons-material";
import {
  FiGrid, FiList, FiCalendar, FiSearch, FiPlus, FiSliders, FiX,
  FiRefreshCw, FiUser, FiSend, FiArchive, FiRotateCcw, FiCheck, FiColumns, FiDownload,
} from "react-icons/fi";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import MasterCommonTable from "../../../sharedComponents/MaterCommonTable/MasterCommonTable";
import TodoCalendarView from "./TodoCalendarView";
import AddTodoTaskModal from "./AddTodoTaskModal";
import RecurringTemplateMain from "../../RecurringTask/RecurringTemplateMain";
import TodoTaskDragSection from "./TodoTaskDragSection";
import Comments from "../../TaskManagement/components/Comments";
import Activity from "../../TaskManagement/components/Activity";
import { PRIORITY, STATUS } from "../../../constant/constant";
import { getTodoTasks, getTodoBoardTasks, updateTodoTask, updateAssigneeStatus, getArchivedTasks, archiveTodoTask, deleteTodoTask, restoreTodoTask, getPendingAcknowledgments, getTaskNoPicker } from "../../../services/todoTask.service";
import PendingAcknowledgmentList from "./PendingAcknowledgmentList";
import { getRecurringTemplates } from "../../../services/recurringTemplate.service";
import { showErrorToast as ToastError, showSuccessToast as ToastSuccess } from "../../../utils/Toaster/AppToast";
import { T, PRI_COLOR, PRI_LABEL, STATUS_LABEL, STATUS_COLOR } from "../constants/todoTokens";
import AssigneeSelect from "../../../components/AssigneeSelect";
import AssigneeGroup from "../../../components/AssigneeGroup";
import { normalizeAssignees, visibleAssignees, isSoloView, isPrivateTask, canSeeAssignees, WORK_STATUSES } from "../../../components/AssigneeGroup/utils";
import PrivateBadge from "../../../components/PrivateBadge";

const TemplateFilter = React.memo(({ selected, onSelect, onClear }) => {
  const [open, setOpen]         = useState(false);
  const [input, setInput]       = useState("");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]   = useState(false);
  const loadedRef = useRef(false);

  const fetchTemplates = useCallback(async () => {
    if (loadedRef.current) return;
    setLoading(true);
    try {
      const res = await getRecurringTemplates({ status: "all", page: 1, limit: 200 });
      if (res?.error === false) {
        setTemplates(res.data || []);
        loadedRef.current = true;
      }
    } catch { /* silent — the picker just stays empty */ }
    finally { setLoading(false); }
  }, []);

  const handleOpen = useCallback(() => { setOpen(true); setInput(""); fetchTemplates(); }, [fetchTemplates]);
  const close = useCallback(() => { setOpen(false); setInput(""); }, []);

  const visible = useMemo(() => {
    const q = input.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) =>
        (t.title || "").toLowerCase().includes(q) ||
        (t.templateNo || "").toLowerCase().includes(q)
    );
  }, [templates, input]);

  return (
    <Box>
      <div
        onClick={() => (open ? close() : handleOpen())}
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 8, cursor: "pointer", border: `1px solid ${open || selected ? "#7C3AED" : "#E2E8F0"}`, background: "#fff", transition: "border-color 0.15s" }}
      >
        {selected ? (
          <>
            <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700, color: "#6D28D9", flexShrink: 0 }}>#{selected.templateNo}</span>
            <span style={{ flex: 1, fontSize: 11, color: "#94A3B8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selected.title}</span>
            <span onClick={(e) => { e.stopPropagation(); onClear(); }} style={{ display: "flex", alignItems: "center", color: "#94A3B8", cursor: "pointer" }}><FiX size={13} /></span>
          </>
        ) : (
          <>
            <span style={{ flex: 1, fontSize: 13, color: "#94A3B8" }}>Any template</span>
            <FiSearch size={13} color="#94A3B8" />
          </>
        )}
      </div>

      {open && (
        <ClickAwayListener onClickAway={close}>
          <div style={{ marginTop: 4, borderRadius: 10, border: "1px solid #E2E8F0", background: "#fff", boxShadow: "0 8px 28px rgba(15,23,42,0.13)", overflow: "hidden", position: "relative", zIndex: 10 }}>
            <div style={{ padding: "8px 8px 4px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 7, border: "1px solid #7C3AED", background: "#F8FAFC" }}>
                <FiSearch size={13} color="#94A3B8" />
                <input
                  autoFocus
                  placeholder="Search templates…"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 13, color: "#0F172A" }}
                />
                {loading && (
                  <svg width="14" height="14" viewBox="0 0 14 14" style={{ flexShrink: 0, animation: "mspin 0.7s linear infinite" }}>
                    <circle cx="7" cy="7" r="5.5" fill="none" stroke="#E2E8F0" strokeWidth="2" />
                    <path d="M7 1.5 A5.5 5.5 0 0 1 12.5 7" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                )}
              </div>
            </div>
            <div style={{ maxHeight: 220, overflowY: "auto", padding: "0 4px 4px" }}>
              {!loading && visible.length === 0 ? (
                <div style={{ padding: "12px 8px", fontSize: 12, color: "#94A3B8", textAlign: "center" }}>No templates found</div>
              ) : (
                visible.map((t) => {
                  const isSel = selected?._id === t._id;
                  const isArchived = t.isArchived || t.status === "archived";
                  return (
                    <div
                      key={t._id}
                      onClick={() => { onSelect(t); close(); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "7px 10px", borderRadius: 7, cursor: "pointer",
                        background: isSel ? "#F5F3FF" : "transparent",
                      }}
                    >
                      <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700, color: "#6D28D9", flexShrink: 0 }}>#{t.templateNo}</span>
                      <span style={{ flex: 1, fontSize: 12, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</span>
                      {isArchived && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: "#94A3B8", border: "1px solid #E2E8F0", borderRadius: 4, padding: "1px 4px", flexShrink: 0 }}>
                          ARCHIVED
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </ClickAwayListener>
      )}
    </Box>
  );
});

const TaskNoFilter = React.memo(({ selected, onSelect, onClear }) => {
  const [open, setOpen]       = useState(false);
  const [input, setInput]     = useState("");
  const [tasks, setTasks]     = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchOptions = useCallback(async (search) => {
    setLoading(true);
    try {
      const res = await getTaskNoPicker(search ? { search } : {});
      if (res?.error === false) {
        setTasks(res.data || []);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  const handleOpen = useCallback(() => { setOpen(true); setInput(""); fetchOptions(""); }, [fetchOptions]);
  const handleInput = useCallback((e) => {
    const val = e.target.value;
    setInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOptions(val), 280);
  }, [fetchOptions]);
  const close = useCallback(() => { setOpen(false); setInput(""); setTasks([]); }, []);

  return (
    <Box>
      <div
        onClick={() => open ? close() : handleOpen()}
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 8, cursor: "pointer", border: `1px solid ${open ? "#0D9488" : "#E2E8F0"}`, background: "#fff", transition: "border-color 0.15s" }}
      >
        {selected ? (
          <>
            <span style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700, color: "#0F766E", flexShrink: 0 }}>#{selected.taskNo}</span>
            <span style={{ flex: 1, fontSize: 11, color: "#94A3B8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selected.title}</span>
            <span onClick={(e) => { e.stopPropagation(); onClear(); }} style={{ display: "flex", alignItems: "center", color: "#94A3B8", cursor: "pointer" }}><FiX size={13} /></span>
          </>
        ) : (
          <>
            <span style={{ flex: 1, fontSize: 13, color: "#94A3B8" }}>Select task no…</span>
            <FiSearch size={13} color="#94A3B8" />
          </>
        )}
      </div>

      {open && (
        <ClickAwayListener onClickAway={close}>
          <div style={{ marginTop: 4, borderRadius: 10, border: "1px solid #E2E8F0", background: "#fff", boxShadow: "0 8px 28px rgba(15,23,42,0.13)", overflow: "hidden", position: "relative", zIndex: 10 }}>
            <div style={{ padding: "8px 8px 4px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 7, border: "1px solid #0D9488", background: "#F8FAFC" }}>
                <FiSearch size={13} color="#94A3B8" />
                <input autoFocus placeholder="Search by task no or title…" value={input} onChange={handleInput}
                  style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 13, color: "#0F172A" }} />
                {loading && (
                  <svg width="14" height="14" viewBox="0 0 14 14" style={{ flexShrink: 0, animation: "mspin 0.7s linear infinite" }}>
                    <circle cx="7" cy="7" r="5.5" fill="none" stroke="#E2E8F0" strokeWidth="2" />
                    <path d="M7 1.5 A5.5 5.5 0 0 1 12.5 7" fill="none" stroke="#0D9488" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                )}
              </div>
            </div>
            <div style={{ maxHeight: 220, overflowY: "auto", padding: "0 4px 4px" }}>
              {!loading && tasks.length === 0 ? (
                <div style={{ padding: "12px 8px", fontSize: 12, color: "#94A3B8", textAlign: "center" }}>No tasks found</div>
              ) : (
                tasks.map((t) => {
                  const isSel = selected?.taskNo === t.taskNo;
                  return (
                    <div key={t._id} onClick={() => { onSelect(t); close(); }}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 7, cursor: "pointer", marginBottom: 2, background: isSel ? "#F0FDFA" : "transparent" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#F0FDFA"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = isSel ? "#F0FDFA" : "transparent"; }}
                    >
                      <span style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 800, color: "#0D9488", flexShrink: 0 }}>#{t.taskNo}</span>
                      <span style={{ flex: 1, fontSize: 12, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</span>
                      {isSel && <CheckOutlined style={{ fontSize: 14, color: "#0D9488" }} />}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </ClickAwayListener>
      )}
    </Box>
  );
});

const BOARD_STATUSES = [
  { _id: STATUS.NOTASSIGNED, name: "Not Assigned", color: "#0D9488" },
  { _id: STATUS.TODO,        name: "To Do",        color: "#64748B" },
  { _id: STATUS.INPROGRESS,  name: "In Progress",  color: "#0D9488" },
  { _id: STATUS.COMPLETED,   name: "Completed",    color: "#22C55E" },
];
const DEFAULT_STATUSES = BOARD_STATUSES;

const FILTER_STATUSES = [
  ...BOARD_STATUSES,
  { _id: 5, name: "Acknowledge",     color: "#0E7490" },
  { _id: 6, name: "Rework Required", color: "#B91C1C" },
  { _id: 7, name: "Hold",            color: "#C2410C" },
  { _id: 8, name: "Duplicate",       color: "#64748B" },
];

const PRIORITY_OPTIONS = [
  { label: "High", value: PRIORITY.HIGH, color: "#DC2626", bg: "#FEF2F2", border: "#FCA5A5" },
  { label: "Medium", value: PRIORITY.MEDIUM, color: "#D97706", bg: "#FFFBEB", border: "#FCD34D" },
  { label: "Low", value: PRIORITY.LOW, color: "#059669", bg: "#ECFDF5", border: "#6EE7B7" },
];


// ─── View Toggle ───────────────────────────────────────────────────────────────
const ViewToggle = ({ view, onChange }) => {
  const opts = [
    { value: "board",    icon: FiGrid,     label: "Board"    },
    { value: "list",     icon: FiList,     label: "List"     },
    { value: "calendar", icon: FiCalendar, label: "Calendar" },
    { value: "archived", icon: FiArchive,  label: "Archived" },
  ];
  return (
    <Box
      sx={{
        display: "inline-flex",
        bgcolor: T.bg,
        borderRadius: "10px",
        p: "3px",
        border: `1px solid ${T.border}`,
      }}
    >
      {opts.map(({ value, icon: Icon, label }) => (
        <Box key={value} sx={{ position: "relative" }}>
          <AnimatePresence>
            {view === value && (
              <motion.div
                layoutId="viewPill"
                style={{
                  position: "absolute", inset: 0,
                  background: T.surface,
                  borderRadius: "7px",
                  boxShadow: T.shadow.sm,
                }}
                transition={{ type: "spring", stiffness: 500, damping: 35 }}
              />
            )}
          </AnimatePresence>
          <Box
            component="button"
            onClick={() => onChange(null, value)}
            sx={{
              position: "relative", zIndex: 1,
              display: "flex", alignItems: "center", gap: "5px",
              px: "12px", py: "7px",
              border: "none", background: "transparent",
              cursor: "pointer", borderRadius: "7px",
              color: view === value ? T.text : T.textMuted,
              fontWeight: view === value ? 700 : 500,
              fontSize: "12px", fontFamily: "inherit",
              transition: "color 0.15s",
              "&:hover": { color: T.text },
            }}
          >
            <Icon size={13} />
            {label}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// ─── Scope Toggle (For Me / By Me) ──────────────────────────────────────────────
const ScopeToggle = ({ scope, onChange }) => {
  const opts = [
    { value: "forme", icon: FiUser, label: "For Me" },
    { value: "byme", icon: FiSend, label: "By Me" },
  ];
  return (
    <Box
      sx={{
        display: "inline-flex",
        bgcolor: T.bg,
        borderRadius: "10px",
        p: "3px",
        border: `1px solid ${T.border}`,
      }}
    >
      {opts.map(({ value, icon: Icon, label }) => (
        <Box key={value} sx={{ position: "relative" }}>
          <AnimatePresence>
            {scope === value && (
              <motion.div
                layoutId="scopePill"
                style={{
                  position: "absolute", inset: 0,
                  background: T.surface,
                  borderRadius: "7px",
                  boxShadow: T.shadow.sm,
                }}
                transition={{ type: "spring", stiffness: 500, damping: 35 }}
              />
            )}
          </AnimatePresence>
          <Box
            component="button"
            onClick={() => onChange(value)}
            sx={{
              position: "relative", zIndex: 1,
              display: "flex", alignItems: "center", gap: "5px",
              px: "12px", py: "7px",
              border: "none", background: "transparent",
              cursor: "pointer", borderRadius: "7px",
              color: scope === value ? T.primaryDark : T.textMuted,
              fontWeight: scope === value ? 700 : 500,
              fontSize: "12px", fontFamily: "inherit",
              transition: "color 0.15s",
              "&:hover": { color: T.text },
            }}
          >
            <Icon size={13} />
            {label}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// ─── Active Filter Chip ────────────────────────────────────────────────────────
const FilterChip = ({ label, onRemove }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8, x: -6 }}
    animate={{ opacity: 1, scale: 1, x: 0 }}
    exit={{ opacity: 0, scale: 0.8, x: -6 }}
    transition={{ type: "spring", stiffness: 420, damping: 24 }}
    layout
  >
    <Box
      sx={{
        display: "inline-flex", alignItems: "center", gap: 0.8,
        pl: 1.4, pr: 0.8, py: "4px",
        bgcolor: T.primaryLight, border: `1px solid ${T.primary}30`,
        borderRadius: "20px", color: T.primaryDark,
        fontSize: "12px", fontWeight: 600,
      }}
    >
      {label}
      <Box
        component="button"
        onClick={onRemove}
        sx={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 16, height: 16, borderRadius: "50%",
          border: "none", background: `${T.primary}20`,
          color: T.primaryDark, cursor: "pointer", p: 0,
          transition: "all 0.15s",
          "&:hover": { background: T.primary, color: "#fff" },
        }}
      >
        <FiX size={9} />
      </Box>
    </Box>
  </motion.div>
);

// ─── Skeleton ──────────────────────────────────────────────────────────────────
const SkeletonCard = ({ delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay }}
    style={{ marginBottom: 10 }}
  >
    <Box sx={{
      p: 2, borderRadius: "12px", bgcolor: T.surface,
      border: `1px solid ${T.border}`, mb: 1.5,
    }}>
      {["85%", "60%", "40%"].map((w, i) => (
        <Box key={i} sx={{
          width: w, height: i === 0 ? 12 : 10, borderRadius: 4, mb: 1,
          bgcolor: i === 0 ? "#E2E8F0" : "#F1F5F9",
          animation: "shimmer 1.6s ease-in-out infinite",
          "@keyframes shimmer": {
            "0%, 100%": { opacity: 0.5 },
            "50%": { opacity: 1 },
          },
          animationDelay: `${delay + i * 0.1}s`,
        }} />
      ))}
    </Box>
  </motion.div>
);

const SkeletonBoard = () => (
  <Box sx={{ display: "flex", gap: 2.5, pt: 1 }}>
    {DEFAULT_STATUSES.map((s, ci) => (
      <Box key={s._id} sx={{ flex: "0 0 320px", borderRadius: "16px", border: `1px solid ${T.border}`, bgcolor: T.surface, overflow: "hidden" }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${T.border}`, bgcolor: T.bg }}>
          <Box sx={{ width: 90, height: 12, borderRadius: 4, bgcolor: "#E2E8F0" }} />
        </Box>
        <Box sx={{ p: 2 }}>
          {[0.1, 0.25, 0.4].map((d) => <SkeletonCard key={d} delay={ci * 0.1 + d} />)}
        </Box>
      </Box>
    ))}
  </Box>
);

const sortByArchivedAt = (arr) =>
  [...arr].sort((a, b) => new Date(b.archivedAt || 0) - new Date(a.archivedAt || 0));

// ─── Archived View ────────────────────────────────────────────────────────────
const ArchivedView = ({ tasks, total, loading, page, onLoadMore, onTaskClick, onRestore }) => {
  if (loading && tasks.length === 0) {
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 2 }}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Box key={i} sx={{ borderRadius: "12px", bgcolor: T.surface, border: `1px solid ${T.border}`, p: 2.5 }}>
            {["75%", "50%", "35%"].map((w, j) => (
              <Box key={j} sx={{ width: w, height: j === 0 ? 13 : 10, borderRadius: 3, mb: 1.2, bgcolor: "#E2E8F0", animation: "shimmer 1.6s ease-in-out infinite", "@keyframes shimmer": { "0%,100%": { opacity: 0.5 }, "50%": { opacity: 1 } }, animationDelay: `${j * 0.1}s` }} />
            ))}
          </Box>
        ))}
      </Box>
    );
  }

  if (tasks.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 12 }}>
        <Box sx={{ width: 64, height: 64, borderRadius: "16px", bgcolor: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
          <FiArchive size={28} color="#CBD5E1" />
        </Box>
        <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#94A3B8" }}>No archived tasks</Typography>
        <Typography sx={{ fontSize: "13px", color: "#CBD5E1", mt: 0.5 }}>Tasks you archive will appear here</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header bar */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "#FFFBEB", border: "1px solid #FDE68A", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FiArchive size={15} color="#D97706" />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: T.text }}>Archived Tasks</Typography>
            <Typography sx={{ fontSize: "11px", color: T.textMuted }}>{total} task{total !== 1 ? "s" : ""} archived</Typography>
          </Box>
        </Stack>
      </Box>

      {/* Grid */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 2 }}>
        {tasks.map((task) => {
          const pri = PRI_COLOR[task.priority] || PRI_COLOR[1];
          const statusCfg = STATUS_COLOR[task.status] || STATUS_COLOR[1];
          return (
            <Box
              key={task._id}
              sx={{
                bgcolor: T.surface, borderRadius: "12px",
                border: `1px solid ${T.border}`,
                p: 2.5,
                transition: "box-shadow 0.15s, border-color 0.15s, transform 0.12s",
                "&:hover": {
                  borderColor: "#D97706",
                  boxShadow: "0 6px 20px rgba(217,119,6,0.12)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              {/* Row 1: task no + status chip */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography
                  onClick={() => onTaskClick(task)}
                  sx={{ fontSize: "10.5px", fontWeight: 700, color: pri.color, letterSpacing: "0.04em", cursor: "pointer" }}
                >
                  #{task.taskNo || "—"}
                </Typography>
                <Chip
                  label={STATUS_LABEL[task.status] || "—"}
                  size="small"
                  sx={{ fontSize: "10px", fontWeight: 700, borderRadius: "6px", height: 20, bgcolor: statusCfg.bg, color: statusCfg.color }}
                />
              </Stack>

              {/* Title */}
              <Typography
                onClick={() => onTaskClick(task)}
                sx={{
                  fontSize: "13px", fontWeight: 600, color: T.text, lineHeight: 1.4, mb: 1,
                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                  cursor: "pointer",
                }}
              >
                {task.title || "Untitled"}
              </Typography>

              <Box sx={{ height: "1px", bgcolor: T.borderLight, mb: 1 }} />

              {/* Priority + deadline */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "7px", py: "2px", borderRadius: "20px", bgcolor: pri.bg }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: pri.color }} />
                  <Typography sx={{ fontSize: "10px", fontWeight: 700, color: pri.color }}>{PRI_LABEL[task.priority] || "Low"}</Typography>
                </Box>
                {task.actualDeadlineDate && (
                  <Typography sx={{ fontSize: "11px", color: T.textMuted }}>
                    Due {dayjs(task.actualDeadlineDate).format("DD MMM YYYY")}
                  </Typography>
                )}
              </Stack>

              {/* Archived meta + Restore button */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={0.6} alignItems="center">
                  {task.archivedByName && (
                    <>
                      <Avatar sx={{ width: 16, height: 16, fontSize: "7px", fontWeight: 700, bgcolor: "#D97706" }}>
                        {task.archivedByName[0]?.toUpperCase()}
                      </Avatar>
                      <Typography sx={{ fontSize: "10px", color: T.textMuted }}>
                        {task.archivedByName}
                      </Typography>
                      {task.archivedAt && (
                        <Typography sx={{ fontSize: "10px", color: "#CBD5E1" }}>
                          · {dayjs(task.archivedAt).format("DD MMM YYYY")}
                        </Typography>
                      )}
                    </>
                  )}
                </Stack>

                {/* Restore button */}
                <Box
                  component="button"
                  onClick={(e) => { e.stopPropagation(); onRestore?.(task); }}
                  sx={{
                    display: "flex", alignItems: "center", gap: "5px",
                    px: "10px", py: "5px",
                    border: `1.5px solid ${T.primary}`,
                    borderRadius: "7px",
                    bgcolor: "transparent",
                    color: T.primary,
                    fontSize: "11px", fontWeight: 700,
                    fontFamily: "inherit", cursor: "pointer",
                    transition: "all 0.15s",
                    "&:hover": { bgcolor: T.primaryLight },
                  }}
                >
                  <FiRotateCcw size={11} />
                  Restore
                </Box>
              </Stack>
            </Box>
          );
        })}
      </Box>

      {/* Load more */}
      {tasks.length < total && (
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Box
            component="button"
            onClick={() => onLoadMore(page + 1)}
            disabled={loading}
            sx={{
              px: 4, py: 1.2,
              border: "1.5px dashed #CBD5E1", borderRadius: "9px",
              bgcolor: "transparent", color: "#64748B",
              fontSize: "12px", fontWeight: 600, fontFamily: "inherit",
              cursor: loading ? "not-allowed" : "pointer",
              "&:hover:not(:disabled)": { bgcolor: "#FFFBEB", borderColor: "#D97706", color: "#D97706" },
            }}
          >
            {loading ? "Loading…" : `Load more (${tasks.length} / ${total})`}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const TodoTaskManagement = () => {
  const navigate = useNav();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [tasksByStatus, setTasksByStatus] = useState({ 1: [], 2: [], 3: [], 4: [] });
  // Ref keeps handleDragEnd stable — no dep on tasksByStatus so DragDropContext never re-renders
  const tasksByStatusRef = useRef({ 1: [], 2: [], 3: [], 4: [] });
  useEffect(() => { tasksByStatusRef.current = tasksByStatus; }, [tasksByStatus]);
  const [hasMoreByStatus, setHasMoreByStatus] = useState({ 1: false, 2: false, 3: false, 4: false });
  const [loadingMoreByStatus, setLoadingMoreByStatus] = useState({ 1: false, 2: false, 3: false, 4: false });
  const [pageByStatus, setPageByStatus] = useState({ 1: 1, 2: 1, 3: 1, 4: 1 });
  const [totalCountByStatus, setTotalCountByStatus] = useState({ 1: 0, 2: 0, 3: 0, 4: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [modalOpen,        setModalOpen]        = useState(false);
  const [recurringOpen,    setRecurringOpen]    = useState(false);
  const [ackDrawerOpen,    setAckDrawerOpen]    = useState(false);
  const [pendingAckCount,  setPendingAckCount]  = useState(0);

  // View is driven by ?view= URL param so it survives page refresh and is shareable
  const VALID_VIEWS = ["board", "list", "calendar", "archived"];
  const view = VALID_VIEWS.includes(searchParams.get("view")) ? searchParams.get("view") : "calendar";
  const setView = useCallback((v) => setSearchParams((prev) => { prev.set("view", v); return prev; }), [setSearchParams]);
  // ── Applied filters — these drive the API (only change on Apply / chip-clear) ─
  const [selectedUsers,   setSelectedUsers]   = useState([]);
  const [memberFilterType,setMemberFilterType]= useState("assignee");
  const [filterDeadlineFrom, setFilterDeadlineFrom] = useState(null);
  const [filterDeadlineTo,   setFilterDeadlineTo]   = useState(null);
  const [filterPriority,  setFilterPriority]  = useState(null);
  const [filterStatus,       setFilterStatus]       = useState([]);
  const [filterFromTemplate,    setFilterFromTemplate]    = useState(false);
  const [filterWithoutTemplate, setFilterWithoutTemplate] = useState(false);
  const [filterTaskNo,          setFilterTaskNo]          = useState(null);
  const [filterTemplate,        setFilterTemplate]        = useState(null);
  // ── Pending filters — drawer UI only, committed on Apply ─────────────────────
  const [pendingUsers,            setPendingUsers]            = useState([]);
  const [pendingMemberType,       setPendingMemberType]       = useState("assignee");
  const [pendingDeadlineFrom,     setPendingDeadlineFrom]     = useState(null);
  const [pendingDeadlineTo,       setPendingDeadlineTo]       = useState(null);
  const [pendingPriority,         setPendingPriority]         = useState(null);
  const [pendingStatus,           setPendingStatus]           = useState([]);
  const [pendingFromTemplate,     setPendingFromTemplate]     = useState(false);
  const [pendingTaskNo,           setPendingTaskNo]           = useState(null);
  const [pendingWithoutTemplate,  setPendingWithoutTemplate]  = useState(false);
  const [pendingTemplate,         setPendingTemplate]         = useState(null);

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [commentsTask, setCommentsTask] = useState(null);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [activityTask, setActivityTask] = useState(null);
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [archivedTotal, setArchivedTotal] = useState(0);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: "", onConfirm: null });
  const [archivedPage, setArchivedPage] = useState(1);
  const [listTasks, setListTasks] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const ALL_COLUMN_IDS = ["taskNo", "serialNo", "description", "status", "priority", "actualDeadlineDate", "_modifiedDeadline", "assigneeName", "supportName", "_remarks"];
  const [visibleColumns, setVisibleColumns] = useState(() => new Set(ALL_COLUMN_IDS));
  const [colPickerAnchor, setColPickerAnchor] = useState(null);
  const [exportDlg, setExportDlg] = useState(false);
  const [exportFrom, setExportFrom] = useState(null);
  const [exportTo, setExportTo] = useState(null);
  const [exportFromErr, setExportFromErr] = useState("");
  const [exportToErr, setExportToErr] = useState("");
  const [exporting, setExporting] = useState(false);
  const [calendarTasks, setCalendarTasks] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const calendarRangeRef = useRef(null);
  // Last page/sort the list table asked for, so a background refresh stays put
  // instead of yanking the user back to page 1.
  const lastListParamsRef = useRef({ page: 1, limit: 10 });
  const [scheduleTasks,       setScheduleTasks]       = useState([]);
  const [scheduleHasMore,     setScheduleHasMore]     = useState(false);
  const [scheduleLoading,     setScheduleLoading]     = useState(false);
  const [scheduleLoadingMore, setScheduleLoadingMore] = useState(false);
  const [scheduleDate,        setScheduleDate]        = useState(() => dayjs());
  const scheduleDateRef       = useRef(dayjs());
  const scheduleCursorRef     = useRef(null);
  const scheduleLoadingMoreRef = useRef(false); // ref mirror — keeps handleScheduleLoadMore stable
  const scheduleHasMoreRef     = useRef(false);
  const [scope, setScope] = useState("forme"); // non-admin: "forme" (assigned to me) | "byme" (created by me)
  const [refreshing, setRefreshing] = useState(false);
  const searchRef = useRef(null);

  const isAdmin = useMemo(() => {
    const isOverAllAdmin = localStorage.getItem("isOverAllAdmin");
    const isAdminFlag    = localStorage.getItem("isAdmin");
    const role           = localStorage.getItem("role");
    return isOverAllAdmin === "true" || isAdminFlag === "true" || role?.toUpperCase() === "FEEDBACK-ADMIN";
  }, []);

  const isPM = useMemo(() => localStorage.getItem("role")?.toUpperCase() === "PROJECT MANAGER", []);
  const isHR = useMemo(() => localStorage.getItem("role")?.toUpperCase() === "HR", []);
  const currentUserId = useMemo(() => localStorage.getItem("userId"), []);

  const filteredStatuses = useMemo(() => {
    let statuses = [...DEFAULT_STATUSES];
    if (!isAdmin) statuses = statuses.filter((s) => s._id !== STATUS.NOTASSIGNED);
    if (filterStatus.length > 0) statuses = statuses.filter((s) => filterStatus.some((f) => f.id === s._id));
    return statuses;
  }, [filterStatus, isAdmin]);

  const activeFilters = [
    selectedUsers.length > 0 && {
      key: "user",
      label: `${memberFilterType === "assignedBy" ? "Assigned by" : "Assignee"}: ` +
        (selectedUsers.length === 1 ? selectedUsers[0].label : `${selectedUsers.length} members`),
      clear: () => { setSelectedUsers([]); setPendingUsers([]); },
    },
    filterPriority && { key: "priority", label: `Priority: ${filterPriority.label}`, clear: () => setFilterPriority(null) },
    (filterDeadlineFrom || filterDeadlineTo) && {
      key: "deadline",
      label: `Deadline: ${filterDeadlineFrom ? filterDeadlineFrom.format("DD/MM/YYYY") : "…"} → ${filterDeadlineTo ? filterDeadlineTo.format("DD/MM/YYYY") : "…"}`,
      clear: () => { setFilterDeadlineFrom(null); setFilterDeadlineTo(null); setPendingDeadlineFrom(null); setPendingDeadlineTo(null); },
    },
    filterStatus.length > 0 && {
      key: "status",
      label: filterStatus.length === 1 ? `Status: ${filterStatus[0].label}` : `Status: ${filterStatus.length} selected`,
      clear: () => { setFilterStatus([]); setPendingStatus([]); },
    },
    filterFromTemplate    && { key: "fromTemplate",    label: "Recurring", clear: () => { setFilterFromTemplate(false);    setPendingFromTemplate(false);    } },
    filterWithoutTemplate && { key: "withoutTemplate", label: "Manual",    clear: () => { setFilterWithoutTemplate(false); setPendingWithoutTemplate(false); } },
    filterTaskNo && { key: "taskNo", label: `Task No: #${filterTaskNo.taskNo}`, clear: () => { setFilterTaskNo(null); setPendingTaskNo(null); } },
    filterTemplate && { key: "template", label: `Template: ${filterTemplate.title}`, clear: () => { setFilterTemplate(null); setPendingTemplate(null); } },
  ].filter(Boolean);

  // Debounce search — wait 1.5s after the user stops typing before querying
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 1500);
    return () => clearTimeout(timer);
  }, [search]);

  const buildBaseParams = useCallback(() => {
    const params = { limit: 20 };
    if (debouncedSearch) params.search = debouncedSearch;
    if (!isAdmin) {
      const userId = localStorage.getItem("userId");
      if (userId) {
        if (scope === "byme") {
          params.assignedBy = userId;           // "By Me": tasks the user created
        } else {
          params.assigneeOrSupport = userId;    // "For Me": tasks where user is assignee OR support
        }
      }
    } else if (selectedUsers.length > 0) {
      // One id serialises exactly as before; several become a comma list the
      // backend reads as $in.
      const memberIds = selectedUsers.map((u) => u._id).join(",");
      if (memberFilterType === "assignedBy") {
        params.assignedBy = memberIds;
      } else {
        params.assignee = memberIds;
      }
    }

    if (filterDeadlineFrom) params.deadlineFrom = filterDeadlineFrom.format("YYYY-MM-DD");
    if (filterDeadlineTo)   params.deadlineTo   = filterDeadlineTo.format("YYYY-MM-DD");
    if (filterPriority) params.priority = filterPriority.value;
    if (filterStatus.length > 0) params.status = filterStatus.map((s) => s.id).join(",");
    if (filterFromTemplate)    params.fromTemplate    = true;
    if (filterWithoutTemplate) params.withoutTemplate = true;
    if (filterTaskNo)          params.taskNo          = filterTaskNo.taskNo;
    if (filterTemplate)        params.recurringTemplateId = filterTemplate._id;
    return params;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, isAdmin, isPM, isHR, scope, selectedUsers, selectedUsers.length ? memberFilterType : null, filterDeadlineFrom, filterDeadlineTo, filterPriority, filterStatus, filterFromTemplate, filterWithoutTemplate, filterTaskNo, filterTemplate]);

  const normalizeTask = (task) => ({
    ...task,
    status: typeof task.status === "number" ? task.status : (parseInt(task.status) || STATUS.TODO),
  });

  const applyBoardResponse = useCallback((res) => {
    if (res?.error !== false) return;
    setTasksByStatus({
      [STATUS.TODO]:        (res.data[STATUS.TODO]        || []).map(normalizeTask),
      [STATUS.INPROGRESS]:  (res.data[STATUS.INPROGRESS]  || []).map(normalizeTask),
      [STATUS.COMPLETED]:   (res.data[STATUS.COMPLETED]   || []).map(normalizeTask),
      [STATUS.NOTASSIGNED]: (res.data[STATUS.NOTASSIGNED] || []).map(normalizeTask),
    });
    setHasMoreByStatus({
      [STATUS.TODO]:        res.hasMore[STATUS.TODO]        ?? false,
      [STATUS.INPROGRESS]:  res.hasMore[STATUS.INPROGRESS]  ?? false,
      [STATUS.COMPLETED]:   res.hasMore[STATUS.COMPLETED]   ?? false,
      [STATUS.NOTASSIGNED]: res.hasMore[STATUS.NOTASSIGNED] ?? false,
    });
    setTotalCountByStatus({
      [STATUS.TODO]:        res.totalCount[STATUS.TODO]        ?? 0,
      [STATUS.INPROGRESS]:  res.totalCount[STATUS.INPROGRESS]  ?? 0,
      [STATUS.COMPLETED]:   res.totalCount[STATUS.COMPLETED]   ?? 0,
      [STATUS.NOTASSIGNED]: res.totalCount[STATUS.NOTASSIGNED] ?? 0,
    });
    setPageByStatus({ [STATUS.TODO]: 1, [STATUS.INPROGRESS]: 1, [STATUS.COMPLETED]: 1, [STATUS.NOTASSIGNED]: 1 });
  }, []);

  useEffect(() => {
    getPendingAcknowledgments({ page: 1, limit: 1 })
      .then((res) => { if (res?.error === false) setPendingAckCount(res.total || 0); })
      .catch(() => {});
  }, []);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTodoBoardTasks({ ...buildBaseParams(), page: 1 });
      applyBoardResponse(res);
    } catch {
      ToastError("Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  }, [buildBaseParams, applyBoardResponse]);

  const silentRefresh = useCallback(async () => {
    try {
      const res = await getTodoBoardTasks({ ...buildBaseParams(), page: 1 });
      applyBoardResponse(res);
    } catch { /* silent — no toast on background refresh */ }
  }, [buildBaseParams, applyBoardResponse]);

  const fetchMoreForStatus = useCallback(async (statusId) => {
    if (loadingMoreByStatus[statusId] || !hasMoreByStatus[statusId]) return;
    const nextPage = pageByStatus[statusId] + 1;
    setLoadingMoreByStatus((prev) => ({ ...prev, [statusId]: true }));
    try {
      const res = await getTodoTasks({ ...buildBaseParams(), page: nextPage, status: statusId });
      if (res?.error === false) {
        setTasksByStatus((prev) => ({
          ...prev,
          [statusId]: [...prev[statusId], ...res.data.map(normalizeTask)],
        }));
        setHasMoreByStatus((prev) => ({ ...prev, [statusId]: res.hasMore ?? false }));
        setPageByStatus((prev) => ({ ...prev, [statusId]: nextPage }));
      }
    } catch {
      ToastError("Failed to load more tasks");
    } finally {
      setLoadingMoreByStatus((prev) => ({ ...prev, [statusId]: false }));
    }
  }, [buildBaseParams, loadingMoreByStatus, hasMoreByStatus, pageByStatus]);

  const fetchListTasks = useCallback(async ({ page = 1, limit = 10, sort, sortType } = {}) => {
    setListLoading(true);
    try {
      const base = buildBaseParams();
      const params = { ...base, page, limit };
      if (sort) params.sort = sort;
      if (sortType) params.sortType = sortType;
      const res = await getTodoTasks(params);
      if (res?.error === false) {
        setListTasks((res.data || []).map(normalizeTask));
        setListTotal(res.totalCount ?? res.total ?? 0);
      }
    } catch {
      ToastError("Failed to fetch tasks");
    } finally {
      setListLoading(false);
    }
  }, [buildBaseParams]);

  const fetchCalendarTasks = useCallback(async () => {
    const range = calendarRangeRef.current;
    if (!range) return;
    setCalendarLoading(true);
    try {
      const res = await getTodoTasks({ ...buildBaseParams(), startDate: range.startDate, endDate: range.endDate, limit: undefined });
      if (res?.error === false) setCalendarTasks((res.data || []).map(normalizeTask));
    } catch {
      ToastError("Failed to fetch tasks");
    } finally {
      setCalendarLoading(false);
    }
  }, [buildBaseParams]);

  // A per-assignee status change can move the task's rolled-up status, so the
  // active view has to re-read. AssigneeGroup already updated its own chip
  // optimistically — this reconciles the column/row the task now belongs in.
  const handleAssigneeStatusChanged = useCallback(() => {
    if (view === "board") silentRefresh();
    else if (view === "list") fetchListTasks(lastListParamsRef.current);
    else if (view === "calendar") fetchCalendarTasks();
  }, [view, silentRefresh, fetchListTasks, fetchCalendarTasks]);

  // Schedule fetches tasks from the selected date onward, cursor = next page number
  // cursor=null  → first page (page=1, no skip)
  // cursor=<str> → backend seek after last doc, no SKIP at all
  const fetchScheduleTasks = useCallback(async (date, cursor = null) => {
    const isReset = cursor === null;
    const from    = date.format("YYYY-MM-DD");
    isReset ? setScheduleLoading(true) : setScheduleLoadingMore(true);
    try {
      const params = {
        ...buildBaseParams(),
        deadlineFrom: from,
        limit: 20,
        sort: "actualDeadlineDate",
        sortType: "asc",
      };
      if (cursor) params.cursor = cursor; // load-more: true cursor seek
      else        params.page   = 1;      // first page: page-based (backend generates cursor for us)

      const res = await getTodoTasks(params);
      if (res?.error === false) {
        const normalized               = (res.data || []).map(normalizeTask);
        const hasMore                  = res.hasMore ?? false;
        scheduleCursorRef.current      = res.nextCursor ?? null; // opaque server cursor
        scheduleHasMoreRef.current     = hasMore;
        scheduleLoadingMoreRef.current = false;
        setScheduleTasks((prev) => {
          if (isReset) return normalized;
          const seen = new Set(prev.map((t) => String(t._id)));
          return [...prev, ...normalized.filter((t) => !seen.has(String(t._id)))];
        });
        setScheduleHasMore(hasMore);
        isReset ? setScheduleLoading(false) : setScheduleLoadingMore(false);
      } else {
        scheduleLoadingMoreRef.current = false;
        isReset ? setScheduleLoading(false) : setScheduleLoadingMore(false);
      }
    } catch {
      ToastError("Failed to fetch schedule");
      scheduleLoadingMoreRef.current = false;
      isReset ? setScheduleLoading(false) : setScheduleLoadingMore(false);
    }
  }, [buildBaseParams]);

  const handleCalendarRangeChange = useCallback((range) => {
    if (range.calView === "schedule") {
      const date = dayjs(range.startDate);
      scheduleDateRef.current        = date;
      scheduleCursorRef.current      = null;
      scheduleHasMoreRef.current     = false;
      scheduleLoadingMoreRef.current = false;
      setScheduleDate(date);
      fetchScheduleTasks(date, null);
      return;
    }
    calendarRangeRef.current = range;
    fetchCalendarTasks();
  }, [fetchCalendarTasks, fetchScheduleTasks]);

  // Stable callback — reads refs instead of state so it never changes reference.
  // This keeps React.memo(ScheduleView) from re-rendering during load.
  const handleScheduleLoadMore = useCallback(() => {
    if (scheduleLoadingMoreRef.current || !scheduleHasMoreRef.current || !scheduleCursorRef.current) return;
    scheduleLoadingMoreRef.current = true;
    fetchScheduleTasks(scheduleDateRef.current, scheduleCursorRef.current); // passes opaque cursor string
  }, [fetchScheduleTasks]);

  const fetchArchivedTasks = useCallback(async (page = 1) => {
    setArchivedLoading(true);
    try {
      const params = { page, limit: 20, sort: "archivedAt", sortType: "desc" };
      // Non-admin: restrict to tasks they created or assigned to them
      if (!isAdmin) {
        const uid = localStorage.getItem("userId");
        if (uid) {
          // All non-admin users see archived tasks where they are assignee OR support
          params.assigneeOrSupport = uid;
        }
      }
      const res = await getArchivedTasks(params);
      if (res?.error === false) {
        const incoming = res.data || [];
        if (page === 1) {
          setArchivedTasks(sortByArchivedAt(incoming));
        } else {
          setArchivedTasks((prev) => sortByArchivedAt([...prev, ...incoming]));
        }
        setArchivedTotal(res.total ?? res.totalCount ?? 0);
        setArchivedPage(page);
      }
    } catch {
      ToastError("Failed to fetch archived tasks");
    } finally {
      setArchivedLoading(false);
    }
  }, []);

  const askConfirm = useCallback((message, onConfirm) => {
    setConfirmDialog({ open: true, message, onConfirm });
  }, []);

  const handleRestoreTask = useCallback((task) => {
    askConfirm(`Restore task #${task.taskNo || task._id}?`, () => {
      setArchivedTasks((prev) => prev.filter((t) => String(t._id) !== String(task._id)));
      setArchivedTotal((prev) => Math.max(0, prev - 1));
      restoreTodoTask(task._id).catch(() => {
        ToastError("Restore failed");
        setArchivedTasks((prev) => sortByArchivedAt([task, ...prev]));
        setArchivedTotal((prev) => prev + 1);
      });
    });
  }, [askConfirm]);

  useEffect(() => { if (view === "board") fetchTasks(); }, [view, fetchTasks]);
  useEffect(() => {
    if (view === "list") fetchListTasks({ page: 1, limit: 10 });
  }, [view, fetchListTasks]);
  // Calendar: re-fetch when filters change (fetchCalendarTasks is recreated whenever buildBaseParams changes)
  useEffect(() => {
    if (view === "calendar") {
      fetchCalendarTasks();
      fetchScheduleTasks(scheduleDateRef.current, null);
    }
  }, [view, fetchCalendarTasks, fetchScheduleTasks]);
  useEffect(() => {
    if (view === "archived") fetchArchivedTasks(1);
  }, [view, fetchArchivedTasks]);

  const handleDragEnd = useCallback(async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;
    if (destination.droppableId === source.droppableId) return;

    const dstId = parseInt(destination.droppableId.replace("status-", ""));
    const srcId = parseInt(source.droppableId.replace("status-", ""));

    // Snapshot from ref — no stale closure, no tasksByStatus in deps
    const prevSnapshot = Object.fromEntries(
      Object.entries(tasksByStatusRef.current).map(([k, v]) => [k, [...v]])
    );

    const draggedTask = tasksByStatusRef.current[srcId]?.find(
      (t) => String(t._id) === String(draggableId)
    );

    // On a private task the task-level status is a ROLLUP of the whole roster,
    // and the server refuses to let a plain assignee write it — that rollup is
    // precisely what privacy hides. Moving their OWN line expresses the same
    // intent and is allowed, so route the drag there instead.
    //
    // Only the three work statuses exist per person. Dragging to Hold, Duplicate,
    // Acknowledge or Rework stays a task-level management action and is still
    // refused for a plain assignee — deliberately, with the server's own
    // "Update your own assignment status instead" surfaced in the toast below.
    const soloMove =
      !!draggedTask &&
      isSoloView(draggedTask, { currentUserId, isAdmin }) &&
      WORK_STATUSES.includes(dstId);

    setTasksByStatus((prev) => {
      const movedTask = prev[srcId]?.find((t) => String(t._id) === String(draggableId));
      if (!movedTask) return prev;
      return {
        ...prev,
        [srcId]: prev[srcId].filter((t) => String(t._id) !== String(draggableId)),
        [dstId]: [...(prev[dstId] || []), { ...movedTask, status: dstId }],
      };
    });

    try {
      const res = soloMove
        ? await updateAssigneeStatus(draggableId, currentUserId, dstId)
        : await updateTodoTask(draggableId, { status: dstId, StatusChange: true });
      if (res?.error !== false) {
        setTasksByStatus(prevSnapshot);
        ToastError(res?.message || "Failed to update status");
      } else {
        ToastSuccess("Status updated");
        // Matters most for a solo move: the card is placed optimistically in the
        // destination column, but what actually moved was one line. The refetch
        // is what reconciles the column with the status the server recomputed.
        silentRefresh();
      }
    } catch (err) {
      setTasksByStatus(prevSnapshot);
      ToastError(err?.data?.message || err?.message || "Error updating status");
    }
  }, [silentRefresh, currentUserId, isAdmin]); // stable — no tasksByStatus dep

  const handleViewChange = (_, val) => setView(val);
  const handleEditTask = useCallback((task) => navigate(`/taskDetails?taskId=${task._id}`), [navigate]);
  const handleViewSourceTask = useCallback((sourceTaskId) => {
    if (sourceTaskId) navigate(`/taskDetails?taskId=${sourceTaskId}`);
  }, [navigate]);
  const handleViewComments = useCallback((task) => {
    setCommentsTask(task);
    setCommentsDialogOpen(true);
  }, []);
  const handleViewActivity = useCallback((task) => {
    setActivityTask(task);
    setActivityDialogOpen(true);
  }, []);
  const removeFromBoard = useCallback((taskId) => {
    setTasksByStatus((prev) => {
      const next = {};
      for (const [sId, sTasks] of Object.entries(prev)) {
        next[sId] = sTasks.filter((t) => String(t._id) !== String(taskId));
      }
      return next;
    });
  }, []);

  const restoreToBoard = useCallback((task) => {
    setTasksByStatus((prev) => ({
      ...prev,
      [task.status]: [task, ...(prev[task.status] || [])],
    }));
  }, []);

  const handleArchiveTask = useCallback((task) => {
    askConfirm(`Archive task #${task.taskNo || task._id}?`, () => {
      removeFromBoard(task._id);
      archiveTodoTask(task._id).catch(() => {
        ToastError("Archive failed");
        restoreToBoard(task);
      });
    });
  }, [removeFromBoard, restoreToBoard, askConfirm]);

  const handleDeleteTask = useCallback((task) => {
    askConfirm(`Delete task #${task.taskNo || task._id}?`, () => {
      removeFromBoard(task._id);
      deleteTodoTask(task._id).catch(() => {
        ToastError("Delete failed");
        restoreToBoard(task);
      });
    });
  }, [removeFromBoard, restoreToBoard, askConfirm]);

  // Stable handlers for calendar + schedule views — removes from both task lists
  // so the task disappears immediately regardless of which view is active.
  const handleCalendarArchive = useCallback((task) => {
    askConfirm(`Archive task #${task.taskNo || task._id}?`, () => {
      const id = String(task._id);
      setCalendarTasks((prev) => prev.filter((t) => String(t._id) !== id));
      setScheduleTasks((prev) => prev.filter((t) => String(t._id) !== id));
      archiveTodoTask(task._id).catch(() => {
        ToastError("Archive failed");
        setCalendarTasks((prev) => [task, ...prev]);
        setScheduleTasks((prev) => [task, ...prev]);
      });
    });
  }, [askConfirm]);

  const handleCalendarDelete = useCallback((task) => {
    askConfirm(`Delete task #${task.taskNo || task._id}?`, () => {
      const id = String(task._id);
      setCalendarTasks((prev) => prev.filter((t) => String(t._id) !== id));
      setScheduleTasks((prev) => prev.filter((t) => String(t._id) !== id));
      deleteTodoTask(task._id).catch(() => {
        ToastError("Delete failed");
        setCalendarTasks((prev) => [task, ...prev]);
        setScheduleTasks((prev) => [task, ...prev]);
      });
    });
  }, [askConfirm]);

  const handleScopeChange = useCallback((newScope) => {
    setScope(newScope);
    setSelectedUsers([]);
    setPendingUsers([]);
  }, []);

  // Opens the drawer and syncs pending state from currently applied state
  const handleOpenFilterDrawer = useCallback(() => {
    setPendingUsers(selectedUsers);
    setPendingMemberType(memberFilterType);

    setPendingPriority(filterPriority);
    setPendingDeadlineFrom(filterDeadlineFrom);
    setPendingDeadlineTo(filterDeadlineTo);
    setPendingStatus(filterStatus);
    setPendingFromTemplate(filterFromTemplate);
    setPendingWithoutTemplate(filterWithoutTemplate);
    setPendingTemplate(filterTemplate);
    setFilterDrawerOpen(true);
  }, [selectedUsers, memberFilterType, filterDeadlineFrom, filterDeadlineTo, filterPriority, filterStatus, filterFromTemplate, filterWithoutTemplate, filterTemplate]);

  // Commits pending → applied and triggers API call
  const handleApplyFilters = useCallback(() => {
    setSelectedUsers(pendingUsers);
    setMemberFilterType(pendingMemberType);
    setFilterDeadlineFrom(pendingDeadlineFrom);
    setFilterDeadlineTo(pendingDeadlineTo);
    setFilterPriority(pendingPriority);
    setFilterStatus(pendingStatus);
    setFilterFromTemplate(pendingFromTemplate);
    setFilterWithoutTemplate(pendingWithoutTemplate);
    setFilterTaskNo(pendingTaskNo);
    setFilterTemplate(pendingTemplate);
    setFilterDrawerOpen(false);
  }, [pendingUsers, pendingMemberType, pendingDeadlineFrom, pendingDeadlineTo, pendingPriority, pendingStatus, pendingFromTemplate, pendingWithoutTemplate, pendingTaskNo, pendingTemplate]);

  // Resets pending state inside the drawer (does NOT apply yet)
  const handleResetPending = useCallback(() => {
    setPendingUsers([]);
    setPendingMemberType("assignee");
    setPendingDeadlineFrom(null);
    setPendingDeadlineTo(null);
    setPendingPriority(null);
    setPendingStatus([]);
    setPendingFromTemplate(false);
    setPendingWithoutTemplate(false);
    setPendingTaskNo(null);
    setPendingTemplate(null);
  }, []);

  // Clears applied filters immediately (chip row "Clear all")
  const clearAllFilters = useCallback(() => {
    setSelectedUsers([]);
    setMemberFilterType("assignee");
    setFilterDeadlineFrom(null);
    setFilterDeadlineTo(null);
    setFilterPriority(null);
    setFilterStatus([]);
    setFilterFromTemplate(false);
    setFilterWithoutTemplate(false);
    setFilterTaskNo(null);
    setFilterTemplate(null);
    setPendingUsers([]);
    setPendingMemberType("assignee");
    setPendingDeadlineFrom(null);
    setPendingDeadlineTo(null);
    setPendingPriority(null);
    setPendingStatus([]);
    setPendingFromTemplate(false);
    setPendingWithoutTemplate(false);
    setPendingTaskNo(null);
    setPendingTemplate(null);
  }, []);

  const handleRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      if (view === "list") await fetchListTasks({ page: 1, limit: 10 });
      else if (view === "calendar") await fetchCalendarTasks();
      else await fetchTasks();
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, view, fetchListTasks, fetchTasks]);

  const handleExportExcel = async () => {
    let valid = true;
    if (!exportFrom) { setExportFromErr("From date is required"); valid = false; }
    else setExportFromErr("");
    if (!exportTo) { setExportToErr("To date is required"); valid = false; }
    else setExportToErr("");
    if (!valid) return;

    setExporting(true);
    try {
      const params = { exportAll: true, deadlineFrom: exportFrom.format("YYYY-MM-DD"), deadlineTo: exportTo.format("YYYY-MM-DD") };
      // carry over active member/scope filters
      if (selectedUsers.length > 0) {
        const memberIds = selectedUsers.map((u) => u._id).join(",");
        if (memberFilterType === "assignedBy") params.assignedBy = memberIds;
        else params.assignee = memberIds;
      }
      const res = await getTodoTasks(params);
      const all = (res?.data || []);

      const ACKNOWLEDGE_STATUS = 5;
      const acknowledged = all.filter((t) => t.status === ACKNOWLEDGE_STATUS);
      const remaining    = all.filter((t) => t.status !== ACKNOWLEDGE_STATUS);

      const stripHtml = (html) => {
        if (!html) return "";
        const d = document.createElement("div");
        d.innerHTML = html;
        return (d.textContent || d.innerText || "").replace(/\s+/g, " ").trim();
      };
      const fmt = (d) => d ? dayjs(d).format("DD MMM YYYY") : "";
      const STATUS_NAMES = { 1: "To Do", 2: "In Progress", 3: "Completed", 4: "Not Assigned", 5: "Acknowledge", 6: "Rework Required", 7: "Hold", 8: "Duplicate" };
      const PRI_NAMES    = { 1: "Low", 2: "Medium", 3: "High" };

      const toRow = (t) => {
        const history = t.deadlineChangeHistory || [];
        const firstDate = history.length > 0 ? fmt(history[0].oldDate) : fmt(t.actualDeadlineDate);
        const modifiedDate = history.length > 0 ? fmt(t.actualDeadlineDate) : "";
        const remarks = history.map((h) => `${fmt(h.newDate)}: ${h.reason || "—"}`).join("\n");
        const people = visibleAssignees(t, { currentUserId, isAdmin });
        return {
          "Task No":           t.taskNo || "",
          "S.No":              t.serialNo || "",
          "Description":       stripHtml(t.description),
          "Status":            STATUS_NAMES[t.status] || "",
          "Priority":          PRI_NAMES[t.priority] || "",
          "Deadline Date":     firstDate,
          "Modified Deadline": modifiedDate,
          // Comma-joined so existing downstream sheets and formulas that read
          // this column keep working; the per-person breakdown gets its own
          // newline-joined column, matching the "Remarks" convention above.
          "Project Manager":   people.map((p) => p.name).join(", "),
          "Assignee Status":   people
            .map((p) => `${p.name} — ${STATUS_NAMES[p.status] || ""}${p.completedAt ? ` (${fmt(p.completedAt)})` : ""}`)
            .join("\n"),
          "With Support Of":   t.supportName || "",
          "Remarks":           remarks,
        };
      };

      const wb = XLSX.utils.book_new();
      const addSheet = (rows, name) => {
        const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{}]);
        // Bold + bg header style via column widths
        const cols = ["Task No","S.No","Description","Status","Priority","Deadline Date","Modified Deadline","Project Manager","Assignee Status","With Support Of","Remarks"];
        ws["!cols"] = [10, 8, 40, 14, 10, 14, 16, 24, 30, 20, 50].map((w) => ({ wch: w }));
        // Freeze top row
        ws["!freeze"] = { xSplit: 0, ySplit: 1 };
        XLSX.utils.book_append_sheet(wb, ws, name);
      };

      addSheet(acknowledged.map(toRow), "Acknowledged");
      addSheet(remaining.map(toRow),    "Remaining");

      const filename = `tasks_${exportFrom.format("DDMMYYYY")}_${exportTo.format("DDMMYYYY")}.xlsx`;
      XLSX.writeFile(wb, filename);
      setExportDlg(false);
      ToastSuccess("Excel downloaded successfully");
    } catch (e) {
      ToastError("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  // Table columns
  const columns = useMemo(() => [
    {
      id: "taskNo", label: "Task No", minWidth: 90,
      render: (val) => (
        <Typography sx={{ fontSize: "11px", fontWeight: 800, color: T.primary, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
          #{val || "—"}
        </Typography>
      ),
    },
    {
      id: "serialNo", label: "S.No", minWidth: 80,
      render: (val) => val ? (
        <Box sx={{ px: "5px", py: "1px", borderRadius: "4px", bgcolor: "#F0F9FF", border: "1px solid #BAE6FD", display: "inline-flex" }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#0369A1", fontFamily: "ui-monospace, SFMono-Regular, monospace", letterSpacing: "0.04em" }}>
            {val}
          </Typography>
        </Box>
      ) : <Typography sx={{ fontSize: "12px", color: T.textMuted }}>—</Typography>,
    },
    {
      id: "description", label: "Description", minWidth: 220,
      render: (val) => {
        if (!val) return <Typography sx={{ fontSize: "12px", color: T.textMuted }}>—</Typography>;
        const plain = (() => { const d = document.createElement("div"); d.innerHTML = val; return (d.textContent || d.innerText || "").replace(/\s+/g, " ").trim(); })();
        return (
          <Tooltip title={plain} placement="top" arrow>
            <Typography sx={{ fontSize: "12px", color: T.textSub, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {plain || "—"}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      id: "status", label: "Status", minWidth: 130,
      render: (val) => {
        const c = STATUS_COLOR[val] || STATUS_COLOR[1];
        return <Chip label={STATUS_LABEL[val] || "—"} size="small" sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, fontSize: "11px", borderRadius: "6px" }} />;
      },
    },
    {
      id: "priority", label: "Priority", minWidth: 100,
      render: (val) => {
        const c = PRI_COLOR[val] || PRI_COLOR[PRIORITY.LOW];
        return <Chip label={PRI_LABEL[val] || "Low"} size="small" sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, fontSize: "11px", borderRadius: "6px" }} />;
      },
    },
    {
      id: "actualDeadlineDate", label: "Deadline Date", minWidth: 120,
      render: (val, row) => {
        const history = row?.deadlineChangeHistory;
        const firstDate = history?.length > 0 ? history[0].oldDate : val;
        return firstDate ? (
          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: T.textSub }}>
            {dayjs(firstDate).format("DD MMM YYYY")}
          </Typography>
        ) : <Typography sx={{ fontSize: "12px", color: T.textMuted }}>—</Typography>;
      },
    },
    {
      id: "_modifiedDeadline", label: "Modified Deadline", minWidth: 130,
      render: (_, row) => {
        const history = row?.deadlineChangeHistory;
        if (!history?.length) return <Typography sx={{ fontSize: "12px", color: T.textMuted }}>—</Typography>;
        return (
          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#C2410C" }}>
            {dayjs(row.actualDeadlineDate).format("DD MMM YYYY")}
          </Typography>
        );
      },
    },
    {
      // The column id stays `assigneeName` so saved column-visibility state and
      // ALL_COLUMN_IDS keep working; the label is business terminology.
      id: "assigneeName", label: "Project Manager", minWidth: 190,
      render: (val, row) => {
        const solo = isSoloView(row, { currentUserId, isAdmin });
        const people = visibleAssignees(row, { currentUserId, isAdmin });
        if (!people.length) {
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar sx={{ width: 26, height: 26, bgcolor: "#E2E8F0", color: "#94A3B8" }}>
                <FiUser size={12} />
              </Avatar>
              <Typography sx={{ fontSize: "13px", color: T.textMuted, fontStyle: "italic" }}>
                Unassigned
              </Typography>
            </Stack>
          );
        }
        const total = people.length;
        const done = people.filter((p) => p.status === STATUS.COMPLETED).length;
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <AssigneeGroup
              task={row}
              taskId={row._id}
              assignedBy={row.assignedBy}
              size={26}
              max={3}
              placement="right"
              isAdmin={isAdmin}
              currentUserId={currentUserId}
              onChanged={handleAssigneeStatusChanged}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: T.textSub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 130 }}>
                {solo ? "You" : total === 1 ? people[0].name : `${total} assignees`}
              </Typography>
              {!solo && total > 1 && (
                <Typography sx={{ fontSize: "10px", fontWeight: 700, color: done === total ? "#15803D" : T.textMuted }}>
                  {done}/{total} done
                </Typography>
              )}
            </Box>
            {canSeeAssignees(row, { currentUserId, isAdmin }) && isPrivateTask(row) && (
              <PrivateBadge compact />
            )}
          </Stack>
        );
      },
    },
    {
      id: "supportName", label: "With Support Of", minWidth: 150,
      render: (val) => {
        const name = (val || "").trim() || null;
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar sx={{ width: 26, height: 26, fontSize: "10px", fontWeight: 700, bgcolor: name ? "#6366F1" : "#E2E8F0", color: name ? "#fff" : "#94A3B8" }}>
              {name ? name[0].toUpperCase() : <FiUser size={12} />}
            </Avatar>
            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: name ? T.textSub : T.textMuted, fontStyle: name ? "normal" : "italic" }}>
              {name || "—"}
            </Typography>
          </Stack>
        );
      },
    },
    {
      id: "_remarks", label: "Remarks", minWidth: 220,
      render: (_, row) => {
        const history = row?.deadlineChangeHistory;
        if (!history?.length) return <Typography sx={{ fontSize: "12px", color: T.textMuted }}>—</Typography>;
        return (
          <Stack direction="column" spacing={0.6}>
            {history.map((h, i) => (
              <Box key={i} sx={{ display: "flex", gap: "6px", alignItems: "flex-start" }}>
                <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#C2410C", whiteSpace: "nowrap", mt: "1px", flexShrink: 0 }}>
                  {dayjs(h.newDate).format("DD MMM YYYY")}
                </Typography>
                <Typography sx={{ fontSize: "10px", color: T.textSub, lineHeight: 1.5 }}>
                  {h.reason || "—"}
                </Typography>
              </Box>
            ))}
          </Stack>
        );
      },
    },
  ], [isAdmin, currentUserId, handleAssigneeStatusChanged]);

  const activeColumns = useMemo(
    () => columns.filter((c) => visibleColumns.has(c.id)),
    [columns, visibleColumns]
  );

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: T.bg }}>

      {/* ── Content area ─────────────────────────────────────────────────────── */}
      <Box sx={{ px: { xs: 1, sm: 1.5, md: 2 }, pt: 1.5, pb: 2 }}>

        {/* ── Toolbar ──────────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.38, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        >
          <Box
            sx={{
              bgcolor: T.surface,
              borderRadius: "14px",
              border: `1px solid ${T.border}`,
              boxShadow: T.shadow.md,
              mb: 2.5,
              overflow: "hidden",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              sx={{ p: "10px 16px" }}
              flexWrap="wrap"
            >
              {/* Left */}
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <ViewToggle view={view} onChange={handleViewChange} />

                {!isAdmin && view !== "archived" && <ScopeToggle scope={scope} onChange={handleScopeChange} />}

                {/* Search */}
                <motion.div
                  animate={{ width: searchFocused ? 260 : 210 }}
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                >
                  <Box
                    sx={{
                      display: "flex", alignItems: "center", gap: 1,
                      px: 1.5, height: "36px",
                      border: `1.5px solid ${searchFocused ? T.primary : T.border}`,
                      borderRadius: "9px",
                      bgcolor: searchFocused ? T.primaryLight : T.bg,
                      boxShadow: searchFocused ? `0 0 0 3px ${T.primaryGlow}` : "none",
                      transition: "border-color 0.2s, background 0.2s, box-shadow 0.2s",
                    }}
                  >
                    <FiSearch size={13} color={searchFocused ? T.primary : T.textMuted} />
                    <Box
                      component="input"
                      ref={searchRef}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onFocus={() => setSearchFocused(true)}
                      onBlur={() => setSearchFocused(false)}
                      placeholder="Search tasks…"
                      sx={{
                        border: "none", background: "transparent", outline: "none",
                        fontSize: "13px", fontWeight: 500, color: T.text, flex: 1,
                        fontFamily: "inherit", width: "100%",
                        "&::placeholder": { color: T.textMuted },
                      }}
                    />
                    <AnimatePresence>
                      {search && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.6 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.6 }}
                          transition={{ duration: 0.12 }}
                        >
                          <Box
                            component="button"
                            onClick={() => setSearch("")}
                            sx={{
                              border: "none", background: "none", cursor: "pointer",
                              display: "flex", alignItems: "center", p: "2px",
                              color: T.textMuted, borderRadius: "4px",
                              "&:hover": { color: T.text, bgcolor: T.borderLight },
                            }}
                          >
                            <IoClose size={13} />
                          </Box>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Box>
                </motion.div>
              </Stack>

              {/* Right */}
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                {/* Refresh — shows the appropriate handler for the active view */}
                <Tooltip title="Refresh" arrow>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={view === "archived" ? () => fetchArchivedTasks(1) : handleRefresh}
                    disabled={view === "archived" ? archivedLoading : refreshing}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      width: 36, height: 36,
                      border: `1.5px solid ${T.border}`,
                      borderRadius: "9px",
                      background: T.surface,
                      cursor: (view === "archived" ? archivedLoading : refreshing) ? "default" : "pointer",
                      color: T.textSub,
                      transition: "all 0.15s",
                    }}
                  >
                    <motion.div
                      animate={(view === "archived" ? archivedLoading : refreshing) ? { rotate: 360 } : { rotate: 0 }}
                      transition={(view === "archived" ? archivedLoading : refreshing) ? { repeat: Infinity, ease: "linear", duration: 0.8 } : { duration: 0.2 }}
                      style={{ display: "flex" }}
                    >
                      <FiRefreshCw size={14} />
                    </motion.div>
                  </motion.button>
                </Tooltip>

                {/* Download Excel — list view only */}
                {view === "list" && (
                  <Tooltip title="Download as Excel">
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => { setExportFrom(filterDeadlineFrom); setExportTo(filterDeadlineTo); setExportFromErr(""); setExportToErr(""); setExportDlg(true); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 6,
                        padding: "7px 14px",
                        border: `1.5px solid #16A34A`,
                        borderRadius: "9px",
                        background: "#F0FDF4",
                        cursor: "pointer", fontFamily: "inherit", fontSize: "12px",
                        fontWeight: 600, color: "#15803D",
                        transition: "all 0.15s",
                      }}
                    >
                      <FiDownload size={13} />
                      Export
                    </motion.button>
                  </Tooltip>
                )}

                {/* Column picker — list view only */}
                {view === "list" && (
                  <Tooltip title="Show / hide columns">
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={(e) => setColPickerAnchor(e.currentTarget)}
                      style={{
                        display: "flex", alignItems: "center", gap: 6,
                        padding: "7px 14px",
                        border: `1.5px solid ${visibleColumns.size < ALL_COLUMN_IDS.length ? T.primary : T.border}`,
                        borderRadius: "9px",
                        background: visibleColumns.size < ALL_COLUMN_IDS.length ? T.primaryLight : T.surface,
                        cursor: "pointer", fontFamily: "inherit", fontSize: "12px",
                        fontWeight: 600,
                        color: visibleColumns.size < ALL_COLUMN_IDS.length ? T.primaryDark : T.textSub,
                        transition: "all 0.15s",
                      }}
                    >
                      <FiColumns size={13} />
                      Columns
                      {visibleColumns.size < ALL_COLUMN_IDS.length && (
                        <Box sx={{ width: 17, height: 17, borderRadius: "50%", bgcolor: T.primary, color: "#fff", fontSize: "10px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {visibleColumns.size}
                        </Box>
                      )}
                    </motion.button>
                  </Tooltip>
                )}

                {view !== "archived" && (
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleOpenFilterDrawer}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "7px 14px",
                      border: `1.5px solid ${activeFilters.length > 0 ? T.primary : T.border}`,
                      borderRadius: "9px",
                      background: activeFilters.length > 0 ? T.primaryLight : T.surface,
                      cursor: "pointer", fontFamily: "inherit", fontSize: "12px",
                      fontWeight: 600, color: activeFilters.length > 0 ? T.primaryDark : T.textSub,
                      transition: "all 0.15s",
                    }}
                  >
                    <FiSliders size={13} />
                    Filters
                    {activeFilters.length > 0 && (
                      <Box sx={{
                        width: 17, height: 17, borderRadius: "50%",
                        bgcolor: T.primary, color: "#fff", fontSize: "10px",
                        fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {activeFilters.length}
                      </Box>
                    )}
                  </motion.button>
                )}

                {view !== "archived" && (
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setRecurringOpen(true)}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "7px 14px",
                      border: `1.5px solid ${T.border}`,
                      borderRadius: "9px",
                      background: T.surface,
                      color: T.textSub, cursor: "pointer",
                      fontFamily: "inherit", fontSize: "13px", fontWeight: 600,
                      transition: "all 0.15s",
                    }}
                  >
                    <FiRefreshCw size={13} />
                    Recurring
                  </motion.button>
                )}

                {/* Pending Acknowledgments badge button */}
                <Tooltip title="Pending Acknowledgments">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setAckDrawerOpen(true)}
                    style={{
                      position: "relative",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      width: 36, height: 36,
                      border: `1.5px solid ${pendingAckCount > 0 ? "#0D9488" : "#E2E8F0"}`,
                      borderRadius: "9px",
                      background: pendingAckCount > 0 ? "#F0FDFA" : "#fff",
                      color: pendingAckCount > 0 ? "#0D9488" : "#64748B",
                      cursor: "pointer", fontFamily: "inherit",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <FiCheck size={15} />
                    {pendingAckCount > 0 && (
                      <Box
                        sx={{
                          position: "absolute", top: -6, right: -6,
                          minWidth: 16, height: 16, borderRadius: "8px",
                          bgcolor: "#EF4444", color: "#fff",
                          fontSize: "9px", fontWeight: 800,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          px: 0.4, border: "1.5px solid #fff",
                        }}
                      >
                        {pendingAckCount > 99 ? "99+" : pendingAckCount}
                      </Box>
                    )}
                  </motion.button>
                </Tooltip>

                {view !== "archived" && (
                  <motion.button
                    whileHover={{ scale: 1.03, boxShadow: T.shadow.primary }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setModalOpen(true)}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "8px 18px",
                      border: "none", borderRadius: "9px",
                      background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
                      color: "#fff", cursor: "pointer",
                      fontFamily: "inherit", fontSize: "13px", fontWeight: 700,
                      boxShadow: "0 2px 8px rgba(13,148,136,0.32)",
                      transition: "box-shadow 0.2s",
                    }}
                  >
                    <FiPlus size={14} />
                    New Task
                  </motion.button>
                )}
              </Stack>
            </Stack>

            {/* Active filter chips row */}
            <AnimatePresence>
              {activeFilters.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.22 }}
                  style={{ overflow: "hidden" }}
                >
                  <Box sx={{ px: 2, pb: 1.5, pt: 0.5, borderTop: `1px solid ${T.borderLight}` }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ pt: 1 }}>
                      <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", mr: 0.5 }}>
                        Filters:
                      </Typography>
                      <AnimatePresence>
                        {activeFilters.map((f) => (
                          <FilterChip key={f.key} label={f.label} onRemove={f.clear} />
                        ))}
                      </AnimatePresence>
                      <motion.button
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        onClick={clearAllFilters}
                        style={{
                          border: `1px solid #FECACA`, borderRadius: "20px",
                          background: "#FEF2F2", color: "#DC2626",
                          fontSize: "11px", fontWeight: 700,
                          padding: "3px 10px", cursor: "pointer", fontFamily: "inherit",
                        }}
                      >
                        Clear all
                      </motion.button>
                    </Stack>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </motion.div>

        {/* ── Board / List content ──────────────────────────────────────────── */}
        <AnimatePresence mode="wait">
          {view === "board" && loading ? (
            <motion.div
              key="skeleton"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <SkeletonBoard />
            </motion.div>
          ) : view === "board" ? (
            <motion.div
              key="board"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
            >
              <DragDropContext onDragEnd={handleDragEnd}>
                <TodoTaskDragSection
                  tasksByStatus={tasksByStatus}
                  hasMoreByStatus={hasMoreByStatus}
                  loadingMoreByStatus={loadingMoreByStatus}
                  totalCountByStatus={totalCountByStatus}
                  onLoadMore={fetchMoreForStatus}
                  statuses={filteredStatuses}
                  onEditTask={handleEditTask}
                  onViewComments={handleViewComments}
                  onViewActivity={handleViewActivity}
                  isAdmin={isAdmin}
                  isPM={isPM}
                  currentUserId={currentUserId}
                  onDeleteTask={handleDeleteTask}
                  onArchiveTask={handleArchiveTask}
                  onViewSourceTask={handleViewSourceTask}
                  onAssigneeStatusChanged={handleAssigneeStatusChanged}
                />
              </DragDropContext>
            </motion.div>
          ) : view === "calendar" ? (
            <motion.div
              key="calendar"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              <TodoCalendarView
                tasks={calendarTasks}
                loading={calendarLoading}
                onTaskClick={handleEditTask}
                onRangeChange={handleCalendarRangeChange}
                isAdmin={isAdmin}
                currentUserId={currentUserId}
                scheduleTasks={scheduleTasks}
                scheduleDate={scheduleDate}
                scheduleLoading={scheduleLoading}
                scheduleLoadingMore={scheduleLoadingMore}
                scheduleHasMore={scheduleHasMore}
                onScheduleLoadMore={handleScheduleLoadMore}
                onArchive={handleCalendarArchive}
                onDelete={handleCalendarDelete}
                onAssigneeStatusChanged={handleAssigneeStatusChanged}
              />
            </motion.div>
          ) : view === "archived" ? (
            <motion.div
              key="archived"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              <ArchivedView
                tasks={archivedTasks}
                total={archivedTotal}
                loading={archivedLoading}
                page={archivedPage}
                onLoadMore={(nextPage) => fetchArchivedTasks(nextPage)}
                onTaskClick={handleEditTask}
                onRestore={handleRestoreTask}
              />
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box
                sx={{
                  bgcolor: T.surface, border: `1px solid ${T.border}`,
                  borderRadius: "14px", overflow: "hidden",
                  boxShadow: T.shadow.md,
                }}
              >
                <MasterCommonTable
                  tableConfig={{ isAction: false, isView: true, isEdit: true, isDelete: false, isSearch: false, isAddNew: false }}
                  columns={activeColumns}
                  data={listTasks}
                  openLoader={listLoading}
                  handleEdit={handleEditTask}
                  handleView={handleEditTask}
                  tableTotalCount={listTotal}
                  isServerSide={true}
                  tableOutputEvent={({ page, limit, sort, sortType }) => {
                    lastListParamsRef.current = { page, limit, sort, sortType };
                    fetchListTasks({ page, limit, sort, sortType });
                  }}
                />
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {/* ── Export Excel dialog ────────────────────────────────────────────────── */}
      <Dialog
        open={exportDlg}
        onClose={() => !exporting && setExportDlg(false)}
        PaperProps={{ sx: { borderRadius: "16px", width: 420, maxWidth: "95vw", p: 0, overflow: "hidden" } }}
      >
        {/* Header */}
        <Box sx={{ background: "linear-gradient(135deg, #16A34A 0%, #15803D 100%)", px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FiDownload size={16} color="#fff" />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#fff" }}>Export to Excel</Typography>
              <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.75)" }}>Select a date range to download the report</Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={() => setExportDlg(false)} disabled={exporting} sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}>
            <FiX size={16} />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 3, py: 3 }}>
          <Stack spacing={2.5}>
            {/* Info banner */}
            <Box sx={{ display: "flex", gap: 1.2, alignItems: "flex-start", bgcolor: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: "10px", px: 2, py: 1.5 }}>
              <FiCalendar size={14} color="#16A34A" style={{ marginTop: 2, flexShrink: 0 }} />
              <Typography sx={{ fontSize: "12px", color: "#15803D", lineHeight: 1.6 }}>
                The report will include <strong>two sheets</strong> — one for Acknowledged tasks, one for all remaining statuses — filtered by the selected deadline date range.
              </Typography>
            </Box>

            {/* Date pickers */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack spacing={2}>
                <Box>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.8 }}>
                    From Date <span style={{ color: "#EF4444" }}>*</span>
                  </Typography>
                  <MuiDatePicker
                    value={exportFrom}
                    onChange={(v) => { setExportFrom(v); if (v) setExportFromErr(""); }}
                    maxDate={exportTo || undefined}
                    slotProps={{
                      textField: {
                        size: "small", fullWidth: true,
                        error: !!exportFromErr,
                        helperText: exportFromErr,
                        sx: { "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "13px" } },
                      },
                    }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.8 }}>
                    To Date <span style={{ color: "#EF4444" }}>*</span>
                  </Typography>
                  <MuiDatePicker
                    value={exportTo}
                    onChange={(v) => { setExportTo(v); if (v) setExportToErr(""); }}
                    minDate={exportFrom || undefined}
                    slotProps={{
                      textField: {
                        size: "small", fullWidth: true,
                        error: !!exportToErr,
                        helperText: exportToErr,
                        sx: { "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "13px" } },
                      },
                    }}
                  />
                </Box>
              </Stack>
            </LocalizationProvider>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1.5 }}>
          <Button
            onClick={() => setExportDlg(false)} disabled={exporting}
            sx={{ borderRadius: "10px", textTransform: "none", borderColor: T.border, color: T.textSub, fontWeight: 600, fontSize: "13px", flex: 1, border: `1px solid ${T.border}`, "&:hover": { borderColor: T.text, bgcolor: T.borderLight } }}
          >
            Cancel
          </Button>
          <motion.button
            whileHover={!exporting ? { scale: 1.02 } : {}}
            whileTap={!exporting ? { scale: 0.98 } : {}}
            onClick={handleExportExcel}
            disabled={exporting}
            style={{
              flex: 2, border: "none", borderRadius: "10px",
              background: exporting ? "#D1FAE5" : "linear-gradient(135deg, #16A34A, #15803D)",
              color: exporting ? "#15803D" : "#fff",
              cursor: exporting ? "default" : "pointer",
              fontFamily: "inherit", fontSize: "13px", fontWeight: 700,
              padding: "10px 20px",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              boxShadow: exporting ? "none" : "0 2px 10px rgba(22,163,74,0.32)",
              transition: "all 0.2s",
            }}
          >
            {exporting ? (
              <>
                <svg width="14" height="14" viewBox="0 0 14 14" style={{ animation: "spin 0.7s linear infinite" }}>
                  <circle cx="7" cy="7" r="5.5" fill="none" stroke="#BBF7D0" strokeWidth="2" />
                  <path d="M7 1.5 A5.5 5.5 0 0 1 12.5 7" fill="none" stroke="#15803D" strokeWidth="2" strokeLinecap="round" />
                  <style>{`@keyframes spin { to { transform-origin: 7px 7px; transform: rotate(360deg); } }`}</style>
                </svg>
                Generating…
              </>
            ) : (
              <><FiDownload size={14} /> Download Excel</>
            )}
          </motion.button>
        </DialogActions>
      </Dialog>

      {/* ── Column picker popover ──────────────────────────────────────────────── */}
      <Popover
        open={Boolean(colPickerAnchor)}
        anchorEl={colPickerAnchor}
        onClose={() => setColPickerAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.5, borderRadius: "12px", boxShadow: "0 8px 32px rgba(15,23,42,0.14)",
            border: `1px solid ${T.border}`, minWidth: 220, p: "12px 4px",
          },
        }}
      >
        <Box sx={{ px: "12px", pb: "8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: T.textSub, textTransform: "uppercase", letterSpacing: "0.07em" }}>
            Columns
          </Typography>
          <Box
            component="button"
            onClick={() => setVisibleColumns(new Set(ALL_COLUMN_IDS))}
            sx={{ fontSize: "11px", fontWeight: 600, color: T.primary, background: "none", border: "none", cursor: "pointer", p: 0 }}
          >
            Reset
          </Box>
        </Box>
        <FormGroup sx={{ px: "8px" }}>
          {columns.map((col) => (
            <FormControlLabel
              key={col.id}
              control={
                <Checkbox
                  size="small"
                  checked={visibleColumns.has(col.id)}
                  onChange={(e) => {
                    setVisibleColumns((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(col.id);
                      else if (next.size > 1) next.delete(col.id);
                      return next;
                    });
                  }}
                  sx={{ color: T.textMuted, "&.Mui-checked": { color: T.primary }, py: "3px" }}
                />
              }
              label={
                <Typography sx={{ fontSize: "13px", fontWeight: 500, color: T.text }}>
                  {col.label}
                </Typography>
              }
              sx={{ mx: 0, "& .MuiFormControlLabel-label": { ml: 0.5 } }}
            />
          ))}
        </FormGroup>
      </Popover>

      {/* ── Filter Drawer ─────────────────────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            bgcolor: T.surface,
            boxShadow: "-8px 0 40px rgba(0,0,0,0.12)",
            borderLeft: `1px solid ${T.border}`,
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Drawer header */}
          <Box
            sx={{
              px: 3, py: 2.5,
              background: `linear-gradient(135deg, #0D9488 0%, #0D9488 100%)`,
              borderBottom: `1px solid rgba(255,255,255,0.06)`,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: "15px", color: "#F8FAFC" }}>
                  Filter Tasks
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#F8FAFC", mt: 0.2 }}>
                  {activeFilters.length > 0 ? `${activeFilters.length} filter${activeFilters.length > 1 ? "s" : ""} active` : "Narrow down your view"}
                </Typography>
              </Box>
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                type="button"
                onClick={() => setFilterDrawerOpen(false)}
                style={{
                  border: "none", background: "rgba(255,255,255,0.08)", borderRadius: "8px",
                  width: 32, height: 32, display: "flex", alignItems: "center",
                  justifyContent: "center", cursor: "pointer", color: "#94A3B8",
                  transition: "all 0.15s",
                }}
              >
                <IoClose size={16} />
              </motion.button>
            </Stack>
          </Box>

          {/* Drawer body */}
          <Box sx={{ flexGrow: 1, px: 3, py: 3, overflowY: "auto" }}>
            <Stack spacing={3.5}>

              {/* Task No filter */}
              <Box>
                <Typography sx={{ fontSize: "10px", fontWeight: 800, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.09em", mb: 1.2 }}>
                  Task Number
                </Typography>
                <TaskNoFilter
                  selected={pendingTaskNo}
                  onSelect={setPendingTaskNo}
                  onClear={() => setPendingTaskNo(null)}
                />
              </Box>

              {/* Member filter — admin only */}
              {isAdmin && (
                <Box>
                  <Typography sx={{ fontSize: "10px", fontWeight: 800, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.09em", mb: 1.2 }}>
                    Member
                  </Typography>

                  {/* Assignee / Assigned By toggle */}
                  <Box
                    sx={{
                      display: "inline-flex",
                      bgcolor: T.bg,
                      borderRadius: "9px",
                      p: "3px",
                      border: `1px solid ${T.border}`,
                      mb: 1.2,
                      width: "100%",
                    }}
                  >
                    {[
                      { value: "assignee", label: "Assigned to" },
                      { value: "assignedBy", label: "Assigned by" },
                    ].map((opt) => (
                      <Box
                        key={opt.value}
                        component="button"
                        onClick={() => setPendingMemberType(opt.value)}
                        sx={{
                          flex: 1,
                          py: "6px",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontFamily: "inherit",
                          fontSize: "12px",
                          fontWeight: 700,
                          transition: "all 0.15s",
                          bgcolor: pendingMemberType === opt.value ? T.surface : "transparent",
                          color: pendingMemberType === opt.value ? T.primaryDark : T.textMuted,
                          boxShadow: pendingMemberType === opt.value ? T.shadow.sm : "none",
                        }}
                      >
                        {opt.label}
                      </Box>
                    ))}
                  </Box>

                  <AssigneeSelect
                    value={pendingUsers.map((u) => u._id)}
                    preloaded={pendingUsers}
                    onChange={(_ids, options) => setPendingUsers(options)}
                    placeholder="Search members…"
                    emptyLabel={pendingMemberType === "assignedBy" ? "Add creators" : "Add assignees"}
                    size={26}
                  />
                </Box>
              )}

              {/* Deadline Date Range — hidden on calendar view (schedule has its own navigation) */}
              {view !== "calendar" && (
                <Box>
                  <Typography sx={{ fontSize: "10px", fontWeight: 800, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.09em", mb: 1.2 }}>
                    Deadline Date
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack spacing={1.2}>
                      <MuiDatePicker
                        label="From"
                        value={pendingDeadlineFrom}
                        onChange={(v) => setPendingDeadlineFrom(v)}
                        maxDate={pendingDeadlineTo || undefined}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            size: "small", fullWidth: true,
                            sx: { "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "13px", "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: T.primary } } },
                          },
                        }}
                      />
                      <MuiDatePicker
                        label="To"
                        value={pendingDeadlineTo}
                        onChange={(v) => setPendingDeadlineTo(v)}
                        minDate={pendingDeadlineFrom || undefined}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            size: "small", fullWidth: true,
                            sx: { "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "13px", "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: T.primary } } },
                          },
                        }}
                      />
                    </Stack>
                  </LocalizationProvider>
                </Box>
              )}

              {/* Priority */}
              <Box>
                <Typography sx={{ fontSize: "10px", fontWeight: 800, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.09em", mb: 1.2 }}>
                  Priority
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {PRIORITY_OPTIONS.map((p) => {
                    const isActive = pendingPriority?.value === p.value;
                    return (
                      <motion.div key={p.value} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Box
                          component="button"
                          onClick={() => setPendingPriority(isActive ? null : p)}
                          sx={{
                            px: 1.8, py: 0.7,
                            border: `1.5px solid ${isActive ? p.color : T.border}`,
                            borderRadius: "8px",
                            bgcolor: isActive ? p.bg : T.surface,
                            color: isActive ? p.color : T.textSub,
                            cursor: "pointer", fontFamily: "inherit",
                            fontSize: "12px", fontWeight: 700,
                            transition: "all 0.15s",
                            "&:hover": { borderColor: p.color, bgcolor: p.bg, color: p.color },
                          }}
                        >
                          {isActive && <CheckOutlined sx={{ fontSize: 11, mr: 0.5, verticalAlign: "middle" }} />}
                          {p.label}
                        </Box>
                      </motion.div>
                    );
                  })}
                </Stack>
              </Box>


              {/* Created from Template */}
              <Box>
                <Typography sx={{ fontSize: "10px", fontWeight: 800, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.09em", mb: 1.2 }}>
                  Source
                </Typography>
                <Stack spacing={0.8}>
                  {/* From template */}
                  <Box
                    component="button"
                    onClick={() => { setPendingFromTemplate((prev) => { if (prev) setPendingTemplate(null); return !prev; }); setPendingWithoutTemplate(false); }}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.5,
                      width: "100%", px: 2, py: 1.2,
                      border: `1.5px solid ${pendingFromTemplate ? "#7C3AED" : T.border}`,
                      borderRadius: "10px",
                      bgcolor: pendingFromTemplate ? "#F5F3FF" : T.surface,
                      cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: "#7C3AED", bgcolor: "#F5F3FF" },
                    }}
                  >
                    <Box sx={{
                      width: 26, height: 26, borderRadius: "7px",
                      bgcolor: pendingFromTemplate ? "#7C3AED" : "#F1F5F9",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, transition: "all 0.15s",
                    }}>
                      <Typography sx={{ fontSize: "10px", fontWeight: 800, color: pendingFromTemplate ? "#fff" : T.textMuted }}>
                        T
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: pendingFromTemplate ? "#6D28D9" : T.textSub, flex: 1 }}>
                      Recurring
                    </Typography>
                    {pendingFromTemplate && <CheckOutlined sx={{ fontSize: 14, color: "#7C3AED" }} />}
                  </Box>

                  {/* Without template */}
                  <Box
                    component="button"
                    onClick={() => { setPendingWithoutTemplate((prev) => !prev); setPendingFromTemplate(false); setPendingTemplate(null); }}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.5,
                      width: "100%", px: 2, py: 1.2,
                      border: `1.5px solid ${pendingWithoutTemplate ? "#0D9488" : T.border}`,
                      borderRadius: "10px",
                      bgcolor: pendingWithoutTemplate ? "#F0FDFA" : T.surface,
                      cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: "#0D9488", bgcolor: "#F0FDFA" },
                    }}
                  >
                    <Box sx={{
                      width: 26, height: 26, borderRadius: "7px",
                      bgcolor: pendingWithoutTemplate ? "#0D9488" : "#F1F5F9",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, transition: "all 0.15s",
                    }}>
                      <Typography sx={{ fontSize: "10px", fontWeight: 800, color: pendingWithoutTemplate ? "#fff" : T.textMuted }}>
                        M
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: pendingWithoutTemplate ? "#0F766E" : T.textSub, flex: 1 }}>
                      Manual
                    </Typography>
                    {pendingWithoutTemplate && <CheckOutlined sx={{ fontSize: 14, color: "#0D9488" }} />}
                  </Box>

                  {/* Narrow to one specific template. Choosing one implies
                      "Recurring", so the two controls can never contradict. */}
                  <Box sx={{ pt: 0.4 }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 600, color: T.textMuted, mb: 0.7 }}>
                      From template
                    </Typography>
                    <TemplateFilter
                      selected={pendingTemplate}
                      onSelect={(t) => { setPendingTemplate(t); setPendingFromTemplate(true); setPendingWithoutTemplate(false); }}
                      onClear={() => setPendingTemplate(null)}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Status */}
              <Box>
                <Typography sx={{ fontSize: "10px", fontWeight: 800, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.09em", mb: 1.2 }}>
                  Status
                </Typography>
                <Stack spacing={0.8}>
                  {FILTER_STATUSES.filter((s) => isAdmin || s._id !== STATUS.NOTASSIGNED).map((s) => {
                    const isActive = pendingStatus.some((f) => f.id === s._id);
                    return (
                      <Box
                        key={s._id}
                        component="button"
                        onClick={() =>
                          setPendingStatus((prev) =>
                            isActive
                              ? prev.filter((f) => f.id !== s._id)
                              : [...prev, { id: s._id, label: s.name }]
                          )
                        }
                        sx={{
                          display: "flex", alignItems: "center", gap: 1.5,
                          px: 2, py: 1,
                          border: `1.5px solid ${isActive ? s.color : T.border}`,
                          borderRadius: "9px",
                          bgcolor: isActive ? `${s.color}12` : T.surface,
                          cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                          transition: "all 0.15s",
                          "&:hover": { borderColor: s.color, bgcolor: `${s.color}10` },
                        }}
                      >
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: s.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: isActive ? s.color : T.textSub, flex: 1 }}>
                          {s.name}
                        </Typography>
                        {isActive && <CheckOutlined sx={{ fontSize: 14, color: s.color }} />}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </Stack>
          </Box>

          {/* Drawer footer */}
          <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${T.border}`, bgcolor: T.bg }}>
            <Stack direction="row" spacing={1.5}>
              <Button
                fullWidth variant="outlined" onClick={handleResetPending}
                sx={{
                  borderRadius: "10px", textTransform: "none",
                  borderColor: T.border, color: T.textSub,
                  fontWeight: 600, fontSize: "13px",
                  "&:hover": { borderColor: T.text, bgcolor: T.borderLight },
                }}
              >
                Reset
              </Button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleApplyFilters}
                style={{
                  flex: 1, border: "none", borderRadius: "10px",
                  background: `linear-gradient(135deg, ${T.primary}, ${T.primaryDark})`,
                  color: "#fff", cursor: "pointer", fontFamily: "inherit",
                  fontSize: "13px", fontWeight: 700, padding: "10px",
                  boxShadow: "0 2px 10px rgba(13,148,136,0.28)",
                }}
              >
                Apply
              </motion.button>
            </Stack>
          </Box>
        </Box>
      </Drawer>

      {/* ── Add Modal ──────────────────────────────────────────────────────────── */}
      <AddTodoTaskModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fetchTasks={view === "calendar" ? fetchCalendarTasks : fetchTasks}
        mode="add"
      />

      {/* ── Recurring Templates — right-side drawer, no nested dialog ─────────── */}
      <Drawer
        anchor="right"
        open={recurringOpen}
        onClose={() => setRecurringOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100vw", sm: 580 },
            bgcolor: "#F1F5F9",
            boxShadow: "-8px 0 40px rgba(0,0,0,0.14)",
            borderLeft: "1px solid #E2E8F0",
          },
        }}
      >
        <RecurringTemplateMain onClose={() => setRecurringOpen(false)} />
      </Drawer>

      {/* ── Comments Dialog ────────────────────────────────────────────────────── */}
      <Dialog
        open={commentsDialogOpen}
        onClose={() => setCommentsDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            background: "linear-gradient(135deg, #0D9488 0%, #0F766E 100%)",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "15px", color: "#fff", lineHeight: 1.2 }}>
                Comments
              </Typography>
              {commentsTask?.title && (
                <Typography
                  sx={{
                    fontSize: "11.5px", color: "rgba(255,255,255,0.75)",
                    mt: 0.3, maxWidth: 360,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}
                >
                  {commentsTask.title}
                </Typography>
              )}
            </Box>
            <IconButton
              onClick={() => setCommentsDialogOpen(false)}
              size="small"
              sx={{
                color: "rgba(255,255,255,0.8)",
                bgcolor: "rgba(255,255,255,0.1)",
                borderRadius: "8px",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)", color: "#fff" },
              }}
            >
              <IoClose size={16} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 0, bgcolor: "#FAFAFA" }}>
          {commentsTask && (
            <Box sx={{ p: 2 }}>
              <Comments
                taskId={commentsTask._id}
                currentUserId={localStorage.getItem("userId")}
                isAdmin={isAdmin}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Activity Dialog ────────────────────────────────────────────────────── */}
      <Dialog
        open={activityDialogOpen}
        onClose={() => setActivityDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isSmallScreen}
        PaperProps={{
          sx: {
            borderRadius: isSmallScreen ? 0 : "16px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ p: 0, background: "linear-gradient(135deg, #0D9488 0%, #0F766E 100%)" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "15px", color: "#fff", lineHeight: 1.2 }}>
                Activity
              </Typography>
              {activityTask?.title && (
                <Typography
                  sx={{
                    fontSize: "11.5px", color: "rgba(255,255,255,0.75)",
                    mt: 0.3, maxWidth: 360,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}
                >
                  {activityTask.title}
                </Typography>
              )}
            </Box>
            <IconButton
              onClick={() => setActivityDialogOpen(false)}
              size="small"
              sx={{
                color: "rgba(255,255,255,0.8)",
                bgcolor: "rgba(255,255,255,0.1)",
                borderRadius: "8px",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)", color: "#fff" },
              }}
            >
              <IoClose size={16} />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: "#FAFAFA", overflow: "hidden" }}>
          {activityTask && (
            <Box sx={{ p: 2 }}>
              <Activity taskId={activityTask._id} />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Confirm Dialog ──────────────────────────────────────────────────────── */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, message: "", onConfirm: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 700, pb: 1 }}>Confirm</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px", color: "#475569" }}>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button
            onClick={() => setConfirmDialog({ open: false, message: "", onConfirm: null })}
            sx={{ textTransform: "none", color: "#64748B" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              confirmDialog.onConfirm?.();
              setConfirmDialog({ open: false, message: "", onConfirm: null });
            }}
            sx={{ textTransform: "none", bgcolor: "#0D9488", "&:hover": { bgcolor: "#0F766E" } }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <PendingAcknowledgmentList
        open={ackDrawerOpen}
        onClose={() => setAckDrawerOpen(false)}
        onCountChange={setPendingAckCount}
        isAdmin={isAdmin}
      />

    </Box>
  );
};

export default TodoTaskManagement;
